import React from 'react';
import {
    Alert,
    AlertTitle,
    Box,
    Typography,
    Chip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    useTheme
} from '@mui/material';
import {
    Error as ErrorIcon,
    Warning as WarningIcon,
    Info as InfoIcon,
    ExpandMore as ExpandMoreIcon,
    Lightbulb as SuggestionIcon
} from '@mui/icons-material';
import logger from '../../utils/logger';

interface ErrorDisplayProps {
    error: string | Error | null;
    context?: string;
    suggestions?: string[];
    severity?: 'error' | 'warning' | 'info';
    showDetails?: boolean;
    onDismiss?: () => void;
}

const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
    error,
    context,
    suggestions = [],
    severity = 'error',
    showDetails = true,
    onDismiss
}) => {
    const theme = useTheme();

    if (!error) return null;

    // Parse error message and extract useful information
    const parseError = (err: string | Error) => {
        let message = '';
        let code = '';
        let reason = '';
        let stack = '';

        if (typeof err === 'string') {
            message = err;
        } else if (err instanceof Error) {
            message = err.message;
            stack = err.stack || '';

            // Try to extract additional info from common error patterns
            if (message.includes('reason:')) {
                const reasonMatch = message.match(/reason:\s*['"]([^'"]+)['"]/);
                if (reasonMatch) {
                    reason = reasonMatch[1];
                }
            }

            if (message.includes('code:')) {
                const codeMatch = message.match(/code:\s*['"]([^'"]+)['"]/);
                if (codeMatch) {
                    code = codeMatch[1];
                }
            }
        }

        return { message, code, reason, stack };
    };

    const { message, code, reason, stack } = parseError(error);

    // Generate smart suggestions based on error patterns
    const generateSmartSuggestions = (errorMessage: string, errorCode: string): string[] => {
        const smartSuggestions: string[] = [...suggestions];

        // ENS-related errors
        if (errorMessage.includes('ENS') || errorMessage.includes('getResolver')) {
            smartSuggestions.push('ENS resolution is disabled in this network configuration');
            smartSuggestions.push('Use direct addresses instead of ENS names');
        }

        // Network errors
        if (errorCode === 'NETWORK_ERROR' || errorMessage.includes('network')) {
            smartSuggestions.push('Check your internet connection');
            smartSuggestions.push('Verify the RPC endpoint is accessible');
            smartSuggestions.push('Try switching to a different network');
        }

        // Transaction errors
        if (errorMessage.includes('insufficient funds')) {
            smartSuggestions.push('Ensure your wallet has enough ETH for gas fees');
            smartSuggestions.push('Check the transaction value and gas settings');
        }

        // Contract interaction errors
        if (errorMessage.includes('execution reverted')) {
            smartSuggestions.push('The smart contract rejected the transaction');
            smartSuggestions.push('Verify all function parameters are correct');
            smartSuggestions.push('Check if you have the required permissions');
        }

        // Address validation errors
        if (errorMessage.includes('invalid address')) {
            smartSuggestions.push('Ensure the address is a valid Ethereum address');
            smartSuggestions.push('Check for typos in the address');
            smartSuggestions.push('Use the contract dropdown to select known addresses');
        }

        // Signature errors
        if (errorMessage.includes('signature') || errorMessage.includes('signer')) {
            smartSuggestions.push('Ensure your wallet is connected');
            smartSuggestions.push('Check if you have the correct signing permissions');
            smartSuggestions.push('Try reconnecting your wallet');
        }

        // Gas estimation errors
        if (errorMessage.includes('gas') && errorMessage.includes('estimate')) {
            smartSuggestions.push('The transaction may fail - check parameters');
            smartSuggestions.push('Try increasing the gas limit manually');
            smartSuggestions.push('Verify the contract function exists and is callable');
        }

        return smartSuggestions;
    };

    const allSuggestions = generateSmartSuggestions(message, code);

    // Determine display message priority with sanitization
    const getDisplayMessage = () => {
        let displayMessage = '';
        if (reason) displayMessage = reason;
        else if (message) displayMessage = message;
        else displayMessage = 'An unknown error occurred';

        // Sanitize the display message to prevent hex data exposure
        const sanitized = logger.sanitizeError({ message: displayMessage }, {
            preserveAddresses: true,
            preserveErrorCodes: true,
            context: context || 'ErrorDisplay'
        });

        return sanitized.message;
    };

    // Get appropriate icon
    const getIcon = () => {
        switch (severity) {
            case 'warning':
                return <WarningIcon />;
            case 'info':
                return <InfoIcon />;
            default:
                return <ErrorIcon />;
        }
    };

    // Log error securely for debugging
    React.useEffect(() => {
        if (error) {
            // Use secure error logging to prevent sensitive data exposure
            logger.secureError(error, context || 'ErrorDisplay', {
                preserveAddresses: true,
                preserveErrorCodes: true,
                logFullErrorServerSide: true
            });
        }
    }, [error, context]);

    return (
        <Alert
            severity={severity}
            icon={getIcon()}
            onClose={onDismiss}
            sx={{ mb: 2 }}
        >
            <AlertTitle>
                {context ? `${context} Error` : 'Error'}
                {code && (
                    <Chip
                        label={code}
                        size="small"
                        variant="outlined"
                        sx={{ ml: 1, fontSize: '0.75rem' }}
                    />
                )}
            </AlertTitle>

            <Typography variant="body2" sx={{ mb: 1 }}>
                {getDisplayMessage()}
            </Typography>

            {/* Suggestions */}
            {allSuggestions.length > 0 && (
                <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <SuggestionIcon sx={{ mr: 1, fontSize: '1rem' }} />
                        Suggestions:
                    </Typography>
                    <List dense sx={{ py: 0 }}>
                        {allSuggestions.map((suggestion, index) => (
                            <ListItem key={index} sx={{ py: 0.25, pl: 0 }}>
                                <ListItemIcon sx={{ minWidth: 20 }}>
                                    <Box
                                        sx={{
                                            width: 4,
                                            height: 4,
                                            borderRadius: '50%',
                                            bgcolor: theme.palette.text.secondary
                                        }}
                                    />
                                </ListItemIcon>
                                <ListItemText
                                    primary={suggestion}
                                    primaryTypographyProps={{
                                        variant: 'body2',
                                        color: 'text.secondary'
                                    }}
                                />
                            </ListItem>
                        ))}
                    </List>
                </Box>
            )}

            {/* Technical Details (collapsible) */}
            {showDetails && (message !== reason || code || stack) && (
                <Accordion sx={{ mt: 2, bgcolor: 'transparent' }} elevation={0}>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{ px: 0, minHeight: 'auto' }}
                    >
                        <Typography variant="caption" color="text.secondary">
                            Technical Details
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 0, pt: 0 }}>
                        {message && message !== reason && (
                            <Box sx={{ mb: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Message:
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontFamily: 'monospace',
                                        fontSize: '0.75rem',
                                        wordBreak: 'break-word',
                                        bgcolor: 'action.hover',
                                        p: 1,
                                        borderRadius: 1,
                                        mt: 0.5
                                    }}
                                >
                                    {message}
                                </Typography>
                            </Box>
                        )}

                        {code && (
                            <Box sx={{ mb: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Error Code:
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                    {code}
                                </Typography>
                            </Box>
                        )}

                        {stack && process.env.NODE_ENV === 'development' && (
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    Stack Trace (Development):
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontFamily: 'monospace',
                                        fontSize: '0.65rem',
                                        wordBreak: 'break-word',
                                        bgcolor: 'action.hover',
                                        p: 1,
                                        borderRadius: 1,
                                        mt: 0.5,
                                        maxHeight: 200,
                                        overflow: 'auto'
                                    }}
                                >
                                    {stack}
                                </Typography>
                            </Box>
                        )}
                    </AccordionDetails>
                </Accordion>
            )}
        </Alert>
    );
};

export default ErrorDisplay;
